import React, { useState, useEffect, useRef } from 'react';
import { ModalStatusType } from '../../../types/common';
import { Form, Formik, FormikProps } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import { YesNoBoolean } from '../../../constants/common';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import _ from 'lodash';
import {
  useGetDestinationCompanies,
  useGetRoleOptions,
  useGetPublishedCompaniesByRole,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { ROLE_PUBLISH_VALIDATION } from '../../../utils/validations/validations';
import { RolePublishedToCompanyService } from '../../../services/wealthlane-identity-services';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';

interface RolePublishModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  rowData?: any;
  id?: string;
}

const RolePublishModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
  id,
}: RolePublishModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: destinationCompaniesOption, isLoading: isDestinationCompaniesOptionLoading } =
    useGetDestinationCompanies(handleRequest);
  const { data: roleOption, isLoading: isRoleOptionLoading } = useGetRoleOptions(handleRequest);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const formikRef = useRef<FormikProps<any>>(null);

  // Fetch published companies for the selected role
  const { data: publishedCompanyIds, isLoading: isPublishedCompaniesLoading } =
    useGetPublishedCompaniesByRole(handleRequest, selectedRoleId);

  // Pre-populate destination companies when published companies data is fetched
  useEffect(() => {
    // Only pre-populate if we have a selected role and the data has been fetched
    if (!formikRef.current || !selectedRoleId || isPublishedCompaniesLoading) return;

    // Pre-populate with fetched published companies
    if (publishedCompanyIds && publishedCompanyIds.length > 0) {
      console.log('Setting destinationCompanyId to:', publishedCompanyIds);
      formikRef.current.setFieldValue('destinationCompanyId', publishedCompanyIds);
    }
  }, [publishedCompanyIds, selectedRoleId, isPublishedCompaniesLoading]);

  const handleSubmit = (values: any): void => {
    setLoading(true);
    const roleId = roleOption?.find((item) => item.value === values?.sourceRoleId).id;

    handleRequest(
      RolePublishedToCompanyService.postApiIdentityApiAppRolePublishedToCompanyManage({
        destinationIds: values?.destinationCompanyId,
        roleId: roleId,
      })
    )
      .then((data: any) => {
        if (data) {
          toast.success(
            <ToastComponent
              label='Success'
              message={
                modalStatus === 'Add' ? 'Role Published successfully.' : 'Role Published edited.'
              }
            />
          );
          queryClient.invalidateQueries('getRolePublishList');
          setLoading(false);
          closeModal();
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });

    // values?.publishId
    //   ? handleRequest(
    //       RolePublishedToCompanyService.patchApiIdentityApiAppRolePublishedToCompany(
    //         values?.publishId,
    //         values
    //       )
    //     )
    //       .then((data: any) => {
    //         if (data) {
    //           toast.success(<ToastComponent label='Success' message='Role Published edited.' />);
    //           queryClient.invalidateQueries('getRolePublishList');
    //           setLoading(false);
    //           closeModal();
    //         }
    //       })
    //       .catch((error: any) => {
    //         console.log('error', error);
    //         setLoading(false);
    //       })
    //       .finally(() => {
    //         setLoading(false);
    //       })
    //   : handleRequest(
    //       RolePublishedToCompanyService.postApiIdentityApiAppRolePublishedToCompany(values)
    //     )
    //       .then((data: any) => {
    //         if (data) {
    //           toast.success(
    //             <ToastComponent label='Success' message='Role Published successfully.' />
    //           );
    //           queryClient.invalidateQueries('getRolePublishList');
    //           setLoading(false);
    //           closeModal();
    //         }
    //       })
    //       .catch((error: any) => {
    //         console.log('error', error);
    //         setLoading(false);
    //       })
    //       .finally(() => {
    //         setLoading(false);
    //       });
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={ROLE_PUBLISH_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({
        touched,
        handleChange,
        errors,
        setFieldValue,
        setFieldTouched,
        values,
        initialValues,
      }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading || loader}
            size='sm'
            id={id}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Role Publish`}
            valueEdited={_.isEqual(values, initialData.data)}
            children={
              <Form>
                <ModalBodyComponent>
                  <div className=''>
                    <FormSelect
                      type='text'
                      label={'Role'}
                      name='sourceRoleId'
                      id='sourceRoleId'
                      containerClassName='formGroup'
                      onChange={(value: string) => {
                        setFieldValue('sourceRoleId', value ?? null);
                        // Reset destination companies when role changes (will be re-populated by useEffect)
                        setFieldValue('destinationCompanyId', null);
                        // Use the customerRoleId (value) as sourceRoleId for the API
                        // The API filters by sourceRoleId which matches customerRoleId
                        setSelectedRoleId(value ?? '');
                      }}
                      errors={modalStatus === 'View' ? {} : errors}
                      touched={touched}
                      options={roleOption}
                      isLoading={isRoleOptionLoading}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isClearable
                      required
                    />
                    {values?.sourceRoleId && (
                      <FormSelect
                        type='text'
                        label={'Company'}
                        name='destinationCompanyId'
                        id='destinationCompanyId'
                        // viewName='messageTemplate'
                        containerClassName='formGroup'
                        onChange={(value: string[]) => {
                          setFieldValue('destinationCompanyId', value ?? null);
                        }}
                        errors={modalStatus === 'View' ? {} : errors}
                        touched={touched}
                        options={destinationCompaniesOption}
                        isLoading={
                          isDestinationCompaniesOptionLoading || isPublishedCompaniesLoading
                        }
                        values={values?.destinationCompanyId}
                        setFieldValue={setFieldValue}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        multiple
                        isClearable
                        required
                      />
                    )}
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  id='rolePublishModalFooter'
                  modalStatus={modalStatus}
                  loading={loading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default RolePublishModal;
